import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Facebook, Youtube, Instagram, Twitter, MapPin, Mail, Phone, Send } from "lucide-react";
import { LOGO_URL, SOCIALS } from "@/constants/branding";
import api, { formatApiErrorDetail } from "@/lib/api";

const iconMap = { Facebook, Youtube, Instagram, Twitter };

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! God bless you.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="mt-32 bg-royal-800 text-white relative overflow-hidden">
      <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
      <div className="container-x py-20 grid gap-14 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CLBC" className="h-14 w-14 rounded-full ring-2 ring-gold/60" />
            <div>
              <div className="font-serif text-xl">Christ Love Breed</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold">Church · CLBC</div>
            </div>
          </div>
          <p className="mt-6 text-white/70 leading-relaxed">
            A Spirit-filled family raising disciples of Christ, demonstrating God's love, and transforming lives through the Gospel.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((s) => {
              const Icon = iconMap[s.icon] || Facebook;
              return (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  className="w-10 h-10 rounded-full border border-white/20 hover:border-gold hover:text-gold flex items-center justify-center transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-gold font-semibold text-sm tracking-[0.2em] uppercase">Quick Links</h4>
          <ul className="mt-6 space-y-3 text-white/80">
            <li><Link to="/about" className="hover:text-gold transition-colors">About Us</Link></li>
            <li><Link to="/overseer" className="hover:text-gold transition-colors">Meet the Overseer</Link></li>
            <li><Link to="/sermons" className="hover:text-gold transition-colors">Sermons</Link></li>
            <li><Link to="/events" className="hover:text-gold transition-colors">Events</Link></li>
            <li><Link to="/give" className="hover:text-gold transition-colors">Give Online</Link></li>
            <li><Link to="/new-here" className="hover:text-gold transition-colors">New Here?</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-gold font-semibold text-sm tracking-[0.2em] uppercase">Reach Us</h4>
          <ul className="mt-6 space-y-4 text-white/80 text-sm">
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-gold" /> CLBC Main Sanctuary,<br />Accra, Ghana</li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-gold" /> +233 (0) 544 154 757</li>
            <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-gold" /> hello@christlovebreedchurch.org</li>
          </ul>
          <div className="mt-6 text-white/70 text-sm">
            <div className="text-gold font-semibold text-xs tracking-[0.2em] uppercase mb-2">Service Times</div>
            <div>Sunday · 12:00 noon</div>
            <div>Thursday · 7:00 PM</div>
          </div>
        </div>

        <div>
          <h4 className="text-gold font-semibold text-sm tracking-[0.2em] uppercase">Stay Encouraged</h4>
          <p className="mt-6 text-white/70 text-sm">
            Get weekly devotionals & event updates straight to your inbox.
          </p>
          <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="footer-newsletter-email"
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-gold"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="w-12 h-12 rounded-full bg-gold hover:bg-gold-500 flex items-center justify-center text-royal-800 transition-colors disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <div>© {new Date().getFullYear()} Christ Love Breed Church. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/prayer" className="hover:text-gold">Prayer Request</Link>
            <Link to="/testimonies" className="hover:text-gold">Testimonies</Link>
            <Link to="/contact" className="hover:text-gold">Contact</Link>
            <Link to="/admin/login" className="hover:text-gold" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
